import { resolveIsFromLanding } from '@/app/reader/utils/reader-perf-timing';

import { tracker } from './dual-tracker';

export { registerTrackerParams, tryStartTrackerFromCachedDevice } from './common-params';

const REFERRER_PAGE_LANDING = 'landing_page';

// client_time	客户端时间戳 (Int)	事件发生的本地时间（13位毫秒）
// current_url	当前页面url(String)	完整的带参链接.
// current_page	当前页面(String)
// referrer_page	来源页面(String)
/*
        当前页面:
            discover // 发现页
            mylist // 书架页
            profile // 个人中心页
            reading_page // 阅读器正文页
            login_page // 登录页
            my_wallet_page // 钱包页
            setting_page // 设置页
            store_page // 充值中心
            topup_history_page // 充值记录明细页 consumption_page // 消耗记录页
            feedback_page // 意见反馈页
            landing_page // 广告落地页

            广告落地入口（冷启动 + 落地参数）时，referrer_page=landing_page
            其他站外来源，referrer_page=external
   */

function mockCurrentPageFromPath(pathname: string): string {
    const p = pathname.toLowerCase() || '/';
    if (p === '/' || p === '' || p.includes('/discover')) {
        return 'discover';
    }
    if (p.includes('/reader')) {
        return 'reading_page';
    }
    if (p.includes('/login')) {
        return 'login_page';
    }
    if (p.includes('/profile')) {
        return 'profile';
    }
    if (p.includes('/my-list')) {
        return 'mylist';
    }
    if (p.includes('/wallet')) {
        return 'my_wallet_page';
    }
    if (p.includes('/settings')) {
        return 'setting_page';
    }
    if (p.includes('/top-up')) {
        return 'topup_history_page';
    }
    if (p.includes('/feedback')) {
        return 'feedback_page';
    }
    if (p.includes('/consumption')) {
        return 'consumption_page';
    }
    if (p.includes('/pay')) {
        return 'store_page';
    }
    return 'discover';
}

/**
 * 仅反映打开当前 HTML 文档时的 HTTP Referer；SPA 站内上一屏由 syncTrackerRoute + buildPageContext 维护。
 */
function referrerPageFromDocument(): string {
    if (typeof document === 'undefined') {
        return '';
    }
    const ref = document.referrer?.trim();
    if (!ref) {
        return '';
    }
    try {
        const refUrl = new URL(ref);
        if (typeof window !== 'undefined') {
            const cur = new URL(window.location.href);
            if (refUrl.origin !== cur.origin) {
                return 'external';
            }
        }
        return mockCurrentPageFromPath(refUrl.pathname);
    } catch {
        return '';
    }
}

/** 与 usePathname() 最近一次同步的值（需在 Root 挂载 TrackerRouteSync） */
let trackerCurrentSyncedPath: string | undefined;
/** router.push 等切页后的「上一屏」pathname，用于映射 referrer_page */
let trackerSpaReferrerSourcePath: string | undefined;

/**
 * 统一的路径同步逻辑：检测到 path 变化时把旧 path 记为「上一屏」。
 * 既会被 TrackerRouteSync 的 effect 调用，也会被 buildPageContext 兜底调用，
 * 因此对调用时机不再敏感（消除「子组件 useLayoutEffect 早于 Root effect」的时序坑）。
 */
function updateSyncedPath(rawPath: string): void {
    const path = rawPath === '' ? '/' : rawPath;
    if (trackerCurrentSyncedPath !== undefined && trackerCurrentSyncedPath !== path) {
        trackerSpaReferrerSourcePath = trackerCurrentSyncedPath;
    }
    trackerCurrentSyncedPath = path;
}

/**
 * 在根布局挂载的路由同步里调用（Next `usePathname`），与 `router.push/replace` 一致，
 * 从而在「上一页从未打点」时仍能拿到上一 path 对应的语义页。
 */
export function syncTrackerRoute(pathname: string): void {
    if (typeof window === 'undefined') {
        return;
    }
    updateSyncedPath(pathname);
}

/**
 * 本次 pathname 同步前的「上一屏」路径（用于 SPA 性能埋点 from_pathname）
 * @description 须在 syncTrackerRoute 之前调用
 */
export function getTrackerSpaReferrerPathname(): string {
    return trackerSpaReferrerSourcePath ?? '';
}

function buildPageContext(): Record<string, unknown> {
    if (typeof window === 'undefined') {
        return {
            client_time: Date.now(),
            current_url: '',
            current_page: '',
            referrer_page: '',
        };
    }

    updateSyncedPath(window.location.pathname);

    const current_page = mockCurrentPageFromPath(trackerCurrentSyncedPath ?? '/');

    let referrer_page = '';
    if (trackerSpaReferrerSourcePath !== undefined) {
        const mappedReferrer = mockCurrentPageFromPath(trackerSpaReferrerSourcePath);
        if (mappedReferrer !== current_page) {
            referrer_page = mappedReferrer;
        }
    } else if (resolveIsFromLanding(new URLSearchParams(window.location.search)) === 1) {
        referrer_page = REFERRER_PAGE_LANDING;
    } else {
        const docReferrer = referrerPageFromDocument();
        if (docReferrer && docReferrer !== current_page) {
            referrer_page = docReferrer;
        }
    }

    return {
        client_time: Date.now(),
        current_url: window.location.href,
        current_page,
        referrer_page,
    };
}

function mergePageParams(extra?: Record<string, unknown>): Record<string, unknown> {
    const pageContext = buildPageContext();
    return {
        ...pageContext,
        ...extra,
        referrer_page: pageContext.referrer_page || 'external',
    };
}

function trackPV(pageKey: string, params?: Record<string, unknown>): void {
    tracker.trackPV(pageKey, mergePageParams(params));
}

function trackClick(eventCode: string, data?: Record<string, unknown>): void {
    tracker.trackClick(eventCode, mergePageParams(data));
}

function trackExposure(eventCode: string, data?: Record<string, unknown>): void {
    tracker.trackExposure(eventCode, mergePageParams(data));
}

function trackCustom(eventCode: string, data?: Record<string, unknown>): void {
    tracker.trackCustom(eventCode, mergePageParams(data));
}

function getReferrerPage(): string {
    return trackerSpaReferrerSourcePath ?? '';
}

export {
    createDualTracker,
    tracker,
    TRACKER_PAGE_SHOW_NAME,
    type IDualTracker,
    type ITrackerUser,
} from './dual-tracker';

export { ETrackerEvents } from './tracker-events';

export { trackPV, trackClick, trackExposure, trackCustom, getReferrerPage };

export { mockCurrentPageFromPath };
